import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

// Import components
import StudentHeader from './components/StudentHeader';
import StudentFilters from './components/StudentFilters';
import StudentActionBar from './components/StudentActionBar';
import StudentTable from './components/StudentTable';
import StudentDetails from './components/StudentDetails';
import BulkOperations from './components/BulkOperations';
import StatusManagement from './components/StatusManagement';

// Import hooks
import { useStudents } from './hooks/useStudents';
import { useStudentOperations } from './hooks/useStudentOperations';
import api from '../../api';

// Import context
import { StudentProvider } from './context/StudentContext';

import TenantAdminLayout from '../Layout/TenantAdminLayout';
import 'react-toastify/dist/ReactToastify.css';

// Main component that uses all the subcomponents
const ManageStudents = () => {
  return (
    <StudentProvider>
      <ManageStudentsContent />
    </StudentProvider>
  );
};

const ManageStudentsContent = () => {
  const { state } = useLocation();
  const tenantId = state?.tenantId;
  const navigate = useNavigate();

  // State for students data - lifted up to parent
  const [students, setStudents] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Prefix management
  const [prefix, setPrefix] = useState('');
  const [prefixInput, setPrefixInput] = useState('');
  const [prefixError, setPrefixError] = useState('');
  const [prefixLoading, setPrefixLoading] = useState(false);
  const [isEditingPrefix, setIsEditingPrefix] = useState(false);

  // USN Prefix management
  const [usnPrefix, setUsnPrefix] = useState('');
  const [usnPrefixInput, setUsnPrefixInput] = useState('');
  const [usnPrefixError, setUsnPrefixError] = useState('');
  const [usnPrefixLoading, setUsnPrefixLoading] = useState(false);
  const [isEditingUsnPrefix, setIsEditingUsnPrefix] = useState(false);

  // New State for Program Lookups
  const [programs, setPrograms] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);

  // Track if initial data has been fetched
  const hasFetchedInitialData = useRef(false);

  // State for bulk delete confirmation
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Use the students hook - FIX: Added setSelectedTerm
  const {
    students: fetchedStudents = [],
    studentsLoading,
    currentPage,
    fetchAllStudentsForTenant,
    setCurrentPage,
    itemsPerPage,
    academicYears = [],
    completedAcademicYears = [],
    selectedAcademicYear,
    classes = [],
    loading: hookLoading,
    isSemesterBased,
    semesters = [],
    terms = [],
    selectedSemester,
    selectedTerm,
    selectedClass,
    searchQuery,
    statusFilter,
    isViewOnly,
    handleAcademicYearChange,
    handleSemesterChange,
    handleTermChange,
    handleClassChange,
    handleSearch,
    setSearchQuery,
    setStatusFilter,
    setSelectedClass,
    setSelectedTerm,          // FIX: Added setSelectedTerm
    academicYearsLoading,
    classesLoading,
  } = useStudents(tenantId);

  // Fetch prefix and program data - only once on mount
  useEffect(() => {
    if (!tenantId || hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;
    const fetchInitialData = async () => {
      setPrefixLoading(true);
      setUsnPrefixLoading(true);
      try {
        const [prefixRes, usnPrefixRes, programsRes, programTypesRes] = await Promise.all([
          api.get(`/api/students/prefix/${tenantId}`).catch(() => ({ data: { prefix: '' } })),
          api.get(`/api/students/tenant/${tenantId}/usn-prefix`).catch(() => ({ data: { usnPrefix: '' } })),
          api.get(`/api/programs?tenantId=${tenantId}`).catch(() => ({ data: { programs: [] } })),
          api.get(`/api/program-types?tenantId=${tenantId}`).catch(() => ({ data: { programTypes: [] } }))
        ]);
        setPrefix(prefixRes.data.prefix || '');
        setUsnPrefix(usnPrefixRes.data.usnPrefix || '');
        setPrograms(programsRes.data.programs || programsRes.data || []);
        setProgramTypes(programTypesRes.data.programTypes || programTypesRes.data || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setPrefixLoading(false);
        setUsnPrefixLoading(false);
      }
    };
    fetchInitialData();
  }, [tenantId]);
  // USN Prefix handlers
  const handleAddUsnPrefix = async () => {
    if (!/^[A-Za-z0-9]+$/.test(usnPrefixInput)) {
      setUsnPrefixError('Must be at least 1 alphanumeric character (A-Z, 0-9)');
      return;
    }
    try {
      setUsnPrefixLoading(true);
      await api.post(`/api/students/tenant/${tenantId}/usn-prefix`, { usnPrefix: usnPrefixInput });
      setUsnPrefix(usnPrefixInput);
      setUsnPrefixInput('');
      setUsnPrefixError('');
    } catch (e) {
      setUsnPrefixError(e.response?.data?.message || 'Error saving USN prefix');
    } finally {
      setUsnPrefixLoading(false);
    }
  };

  const handleDeleteUsnPrefix = async () => {
    try {
      setUsnPrefixLoading(true);
      await api.delete(`/api/students/tenant/${tenantId}/usn-prefix`);
      setUsnPrefix('');
    } finally {
      setUsnPrefixLoading(false);
    }
  };

  const handleUpdateUsnPrefix = async (newValue) => {
    if (!/^[A-Za-z0-9]+$/.test(newValue)) {
      setUsnPrefixError('Must be at least 1 alphanumeric character (A-Z, 0-9)');
      return;
    }
    setUsnPrefixLoading(true);
    try {
      await api.put(`/api/students/tenant/${tenantId}/usn-prefix`, { usnPrefix: newValue });
      setUsnPrefix(newValue);
      setUsnPrefixInput('');
      setUsnPrefixError('');
    } catch (err) {
      setUsnPrefixError(err.response?.data?.message || 'Error updating USN prefix');
    } finally {
      setUsnPrefixLoading(false);
    }
  };

  // Fetch students - only when tenantId changes or on page change
  useEffect(() => {
    if (!tenantId) return;
    
    const token = localStorage.getItem('token');
    if (token) {
      fetchAllStudentsForTenant(currentPage);
    }
  }, [tenantId, currentPage, fetchAllStudentsForTenant]);

  // Initialize students state when fetchedStudents changes
  useEffect(() => {
    setStudents(fetchedStudents || []);
    if (isInitialLoad && fetchedStudents.length >= 0) {
      setIsInitialLoad(false);
    }
  }, [fetchedStudents, isInitialLoad]);

  const handleAddPrefix = async () => {
    if (!/^[A-Z]{4}$/.test(prefixInput)) {
      setPrefixError('Must be exactly 4 uppercase letters');
      return;
    }
    try {
      setPrefixLoading(true);
      await api.post(`/api/students/prefix/${tenantId}`, { prefix: prefixInput });
      setPrefix(prefixInput);
      setPrefixInput('');
      setPrefixError('');
    } catch (e) {
      setPrefixError(e.response?.data?.message || 'Error saving prefix');
    } finally {
      setPrefixLoading(false);
    }
  };

  const handleDeletePrefix = async () => {
    try {
      setPrefixLoading(true);
      await api.delete(`/api/students/prefix/${tenantId}`);
      setPrefix('');
    } finally {
      setPrefixLoading(false);
    }
  };

  const handleUpdatePrefix = async (newValue) => {
    if (!/^[A-Z]{4}$/.test(newValue)) {
      setPrefixError('Must be exactly 4 uppercase letters');
      return;
    }
    setPrefixLoading(true);
    try {
      await api.put(`/api/students/prefix/${tenantId}`, { prefix: newValue });
      setPrefix(newValue);
      setPrefixInput('');
      setPrefixError('');
    } catch (err) {
      setPrefixError(err.response?.data?.message || 'Error updating prefix');
    } finally {
      setPrefixLoading(false);
    }
  };

  // Filtering logic - Use authStatus for filtering by status
  const filteredStudents = students.filter(student => {
    if (selectedAcademicYear && student.academicYearId !== selectedAcademicYear) return false;
    if (isSemesterBased && selectedSemester && student.semesterId !== selectedSemester) return false;
    if (!isSemesterBased && selectedTerm && student.termId !== selectedTerm) return false;
    if (selectedClass && student.classId !== selectedClass) return false;
    // Filter by authStatus instead of student.status
    if (statusFilter && student.authStatus !== statusFilter) return false;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const match =
        (student.firstName && student.firstName.toLowerCase().includes(q)) ||
        (student.lastName && student.lastName.toLowerCase().includes(q)) ||
        (student.email && student.email.toLowerCase().includes(q)) ||
        (student.admissionNumber && student.admissionNumber.toLowerCase().includes(q)) ||
        (student.className && student.className.toLowerCase().includes(q)) ||
        (student.rollNumber && String(student.rollNumber).toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });

  // Show all students without pagination
  const paginatedStudents = filteredStudents;
  const totalPages = 1;

  // Enhanced student operations with state updates
  const {
    showBulkAddModal,
    setShowBulkAddModal,
    showBulkDeleteModal,
    setShowBulkDeleteModal,
    handleBulkAddStudents,
    handleBulkDelete,
    bulkAddLoading,
    bulkDeleteLoading,
    viewStudentData,
    setViewStudentData,
    handleDeleteStudent,
    confirmDeleteId,
    setConfirmDeleteId,
    deleteLoadingId,
    selectedStudentIds = [],
    setSelectedStudentIds,
  } = useStudentOperations(tenantId, students, setStudents);

  // Refresh students data - memoized to prevent recreation
  const refreshStudentsData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token && tenantId) {
      await fetchAllStudentsForTenant(currentPage);
    }
  }, [tenantId, currentPage, fetchAllStudentsForTenant]); 

  // Navigation handlers for add and edit
  const handleAddStudentNavigation = () => {
    if (isViewOnly) {
      toast.error('Cannot add students to completed academic year');
      return;
    }
    const token = localStorage.getItem('token');
    navigate('/tenant-admin/add-student', {
      state: {
        tenantId,
        academicYearId: selectedAcademicYear,
        isSemesterBased,
        semesterId: selectedSemester,
        termId: selectedTerm,
        classId: selectedClass,
        isViewOnly,
        from: -1,
        token
      }
    });
  };

  const handleEditStudentNavigation = (student) => {
    navigate(`/tenant-admin/edit-student/${student.studentId || student._id}`, {
      state: {
        tenantId,
        studentData: student,
        academicYearId: selectedAcademicYear,
        isSemesterBased,
        semesterId: selectedSemester,
        termId: selectedTerm,
        classId: selectedClass,
        isViewOnly
      }
    });
  };

  // Enhanced handlers that update local state
  const enhancedHandleDeleteStudent = async (studentId, tenantIdArg) => {
    try {
      console.log(`🔄 Enhanced delete called for student: ${studentId} with tenant: ${tenantIdArg || tenantId}`);
      await handleDeleteStudent(studentId, tenantIdArg);
      await refreshStudentsData();
      toast.success('Student deleted successfully!');
    } catch (error) {
      console.error('❌ Delete failed:', error);
      toast.error(error.message || 'Failed to delete student');
      throw error;
    }
  };

  const enhancedHandleBulkAddStudents = async (studentsList) => {
    try {
      await handleBulkAddStudents(studentsList);
      await refreshStudentsData();
      toast.success('Bulk students added successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to add bulk students');
      throw error;
    }
  };

  const enhancedHandleBulkDelete = async (tenantIdArg) => {
    try {
      await handleBulkDelete(tenantIdArg);
      await refreshStudentsData();
      toast.success('Selected students deleted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to delete students');
      throw error;
    }
  };

  // Update status handler to use authStatus
  const handleStatusUpdate = async (studentId, newStatus) => {
    const token = localStorage.getItem('token');
    const student = students.find(s => (s.studentId || s._id) === studentId);
    const tenantIdToSend = student?.tenantId || tenantId;
    try {
      if (!tenantIdToSend) {
        toast.error('Tenant ID is missing. Cannot update status.');
        return;
      }
      const response = await api.put(
        `/api/students/${studentId}/status`,
        {
          status: newStatus,
          tenantId: tenantIdToSend
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (response.status === 200) {
        setStudents(prev =>
          prev.map(student =>
            (student.studentId || student._id) === studentId
              ? { ...student, authStatus: newStatus }
              : student
          )
        );
        setViewStudentData(prev =>
          prev && (prev.studentId || prev._id) === studentId
            ? { ...prev, authStatus: newStatus }
            : prev
        );
        toast.success(`Student status updated to ${newStatus}`);
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update student status');
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (newStatus) => {
    if (!selectedStudentIds || selectedStudentIds.length === 0) {
      toast.warning('No students selected for status update');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.put('/api/students/bulk-update-status', {
        studentIds: selectedStudentIds,
        status: newStatus,
        tenantId: tenantId
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        setStudents(prev =>
          prev.map(student =>
            selectedStudentIds.includes(student.studentId || student._id)
              ? { ...student, authStatus: newStatus }
              : student
          )
        );
        setSelectedStudentIds([]);
        toast.success(`Updated ${selectedStudentIds.length} students to ${newStatus}`);
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Bulk status update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update student status');
    }
  };

  // Calculate statistics using authStatus
  const activeStudentCount = students.filter(s => s.authStatus === 'active').length;
  const inactiveStudentCount = students.filter(s => s.authStatus === 'inactive').length;
  const totalStudentCount = students.length;

  // Combined loading state logic
  const isLoading = 
    hookLoading || 
    academicYearsLoading || 
    classesLoading || 
    (isInitialLoad && !hasFetchedInitialData.current) ||
    (isInitialLoad && academicYears.length === 0 && completedAcademicYears.length === 0);

  // Only show "no academic years" when loading is complete AND there are truly no academic years
  const noAcademicYears = 
    !isLoading && 
    (!academicYears || academicYears.length === 0) && 
    (!completedAcademicYears || completedAcademicYears.length === 0);

  // Helper to sanitize student data
  const getSanitizedStudentData = (student) => {
    if (!student) return null;
    
    let emergencyContactString = student.emergencyContact;
    if (typeof student.emergencyContact === 'object' && student.emergencyContact !== null) {
      const { relationship, countryCode, number } = student.emergencyContact;
      const parts = [];
      if (relationship) parts.push(`${relationship}:`);
      if (countryCode) parts.push(countryCode);
      if (number) parts.push(number);
      emergencyContactString = parts.length > 0 ? parts.join(' ') : '-';
    }

    return {
      ...student,
      emergencyContact: emergencyContactString
    };
  };

  // Show loading only when truly loading (not just initial load)
  if (isLoading) {
    return (
      <TenantAdminLayout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-6">
              {/* Header skeleton */}
              <div>
                <div className="h-10 bg-white rounded-xl w-1/3 mb-4 shadow-sm"></div>
                <div className="h-4 bg-white rounded w-1/2 mb-6 shadow-sm"></div>
              </div>
              
              {/* Stats skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-28 bg-white rounded-2xl shadow-sm"></div>
                ))}
              </div>
              
              {/* Filters skeleton */}
              <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4 mb-6">
                <div className="h-10 bg-gray-100 rounded-lg w-full"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="h-12 bg-gray-100 rounded-lg"></div>
                  <div className="h-12 bg-gray-100 rounded-lg"></div>
                  <div className="h-12 bg-gray-100 rounded-lg"></div>
                </div>
              </div>
              
              {/* Table skeleton */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="h-12 bg-gray-100 rounded-lg mb-6"></div>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-50 rounded-lg mb-4"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  // Show "No Academic Years" only after loading is complete
  if (noAcademicYears) {
    return (
      <TenantAdminLayout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
          <div className="max-w-3xl mx-auto p-6">
            <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-red-500">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <svg className="h-10 w-10 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-6">
                  <h3 className="text-2xl font-bold text-red-800 mb-3">No Academic Years Found</h3>
                  <p className="text-red-600 mb-6">
                    You need to create at least one academic year before you can manage students.
                  </p>
                  <div className="mt-8">
                    <button 
                      onClick={() => navigate('/tenant-admin/academic-years', { state: { tenantId, userId: state?.userId } })}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl shadow-sm text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Go to Academic Years
                    </button>
                    <button 
                      onClick={() => navigate(-1)}
                      className="ml-4 inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  return (
    <TenantAdminLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <ToastContainer 
          position="top-right" 
          autoClose={3000} 
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header Section */}
          <StudentHeader 
            tenantId={tenantId}
            prefix={prefix}
            prefixInput={prefixInput}
            setPrefixInput={setPrefixInput}
            prefixError={prefixError}
            prefixLoading={prefixLoading}
            addPrefix={handleAddPrefix}
            deletePrefix={handleDeletePrefix}
            updatePrefix={handleUpdatePrefix}
            isEditingPrefix={isEditingPrefix}
            setIsEditingPrefix={setIsEditingPrefix}
            usnPrefix={usnPrefix}
            usnPrefixInput={usnPrefixInput}
            setUsnPrefixInput={setUsnPrefixInput}
            usnPrefixError={usnPrefixError}
            usnPrefixLoading={usnPrefixLoading}
            addUsnPrefix={handleAddUsnPrefix}
            deleteUsnPrefix={handleDeleteUsnPrefix}
            updateUsnPrefix={handleUpdateUsnPrefix}
            isEditingUsnPrefix={isEditingUsnPrefix}
            setIsEditingUsnPrefix={setIsEditingUsnPrefix}
            activeStudentCount={activeStudentCount}
            inactiveStudentCount={inactiveStudentCount}
            totalStudentCount={totalStudentCount}
          />

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4 sticky top-4 z-20">
            <div className="flex-1">
              <StudentFilters
                academicYears={academicYears}
                completedAcademicYears={completedAcademicYears}
                selectedAcademicYear={selectedAcademicYear}
                onAcademicYearChange={handleAcademicYearChange}
                isSemesterBased={isSemesterBased}
                semesters={semesters}
                selectedSemester={selectedSemester}
                onSemesterChange={handleSemesterChange}
                terms={terms}
                selectedTerm={selectedTerm}
                onTermChange={handleTermChange}
                classes={classes}
                selectedClass={selectedClass}
                onClassChange={handleClassChange}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                onClearFilters={() => {
                  setSelectedClass('');
                  setStatusFilter('');
                  if (!isSemesterBased) setSelectedTerm(''); // FIX: Using setSelectedTerm
                }}
                isLoading={classesLoading || academicYearsLoading}
              />
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                onClick={() => {
                  setSelectedClass('');
                  setStatusFilter('');
                  if (!isSemesterBased) setSelectedTerm(''); // FIX: Using setSelectedTerm
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium border border-blue-100 bg-blue-50 px-4 py-2 rounded-lg transition"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* View Only Warning */}
          {isViewOnly && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center">
              <svg className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-yellow-800 font-medium">View Only Mode</p>
                <p className="text-yellow-700 text-sm">You are viewing a completed academic year. Adding or editing students is disabled.</p>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Action Bar */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <StudentActionBar
                  tenantId={tenantId}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onSearch={handleSearch}
                  onAddStudent={handleAddStudentNavigation}
                  onBulkAdd={() => setShowBulkAddModal(true)}
                  hasPrefix={!!prefix && !!usnPrefix}
                  selectedCount={selectedStudentIds.length}
                  onViewStudent={setViewStudentData}
                  onEditStudent={handleEditStudentNavigation}
                  onDeleteStudent={setConfirmDeleteId}
                  isViewOnly={isViewOnly}
                />
            </div>

            {/* Bulk Actions */}
            {selectedStudentIds.length > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-blue-900">
                        {selectedStudentIds.length} students selected
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusManagement
                      selectedStudentIds={selectedStudentIds}
                      onStatusUpdate={() => {
                        refreshStudentsData();
                        setSelectedStudentIds([]);
                      }}
                      tenantId={tenantId}
                      onBulkStatusUpdate={handleBulkStatusUpdate}
                    />
                    
                    <button
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      disabled={selectedStudentIds.length === 0 || isViewOnly}
                      className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Selected
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Student Table */}
            <div className="p-6">
              <StudentTable
                students={paginatedStudents}
                studentsLoading={studentsLoading}
                selectedStudentIds={selectedStudentIds}
                onSelectStudent={setSelectedStudentIds}
                onViewStudent={setViewStudentData}
                onEditStudent={handleEditStudentNavigation}
                onDeleteStudent={(id) => {
                  console.log(`🖱️ Delete clicked for student: ${id} with tenant: ${tenantId}`);
                  setConfirmDeleteId(id);
                }}
                onStatusChange={handleStatusUpdate}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalStudents={filteredStudents.length}
                isViewOnly={isViewOnly}
                tenantId={tenantId} 
              />
            </div>
          </div>

          {/* View Student Details Modal */}
          {viewStudentData && (
            <StudentDetails
              student={getSanitizedStudentData(viewStudentData)}
              academicYears={academicYears}
              onClose={() => setViewStudentData(null)}
              onStatusChange={handleStatusUpdate}
              programs={programs}
              programTypes={programTypes}
            />
          )}

          {/* Bulk Operations Modal */}
          <BulkOperations
            selectedStudentIds={selectedStudentIds}
            showBulkAddModal={showBulkAddModal}
            onCloseBulkAdd={() => setShowBulkAddModal(false)}
            onBulkAdd={enhancedHandleBulkAddStudents}
            onBulkAddComplete={refreshStudentsData}
            bulkAddLoading={bulkAddLoading}
            showBulkDeleteModal={showBulkDeleteModal}
            openBulkDeleteModal={() => setShowBulkDeleteModal(true)}
            onCloseBulkDelete={() => setShowBulkDeleteModal(false)}
            onBulkDelete={enhancedHandleBulkDelete}
            bulkDeleteLoading={bulkDeleteLoading}
            academicYears={academicYears}
            tenantId={tenantId}
            selectedAcademicYear={selectedAcademicYear}
            onBulkStatusUpdate={handleBulkStatusUpdate}
            isViewOnly={isViewOnly}
          />

          {/* Single Delete Confirmation Modal */}
          {confirmDeleteId && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full transform transition-all duration-200 scale-100">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800 text-center mb-2">Confirm Delete</h2>
                <p className="text-gray-600 text-center mb-6">Are you sure you want to delete this student? This action cannot be undone.</p>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                    disabled={deleteLoadingId === confirmDeleteId}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      console.log(`✅ Delete confirmed for student: ${confirmDeleteId} with tenant: ${tenantId}`);
                      await enhancedHandleDeleteStudent(confirmDeleteId, tenantId);
                      setConfirmDeleteId(null);
                    }}
                    className="px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center"
                    disabled={deleteLoadingId === confirmDeleteId}
                  >
                    {deleteLoadingId === confirmDeleteId ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Deleting...
                      </>
                    ) : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Delete Confirm Modal */}
          {showBulkDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-bold text-gray-800 mb-2 text-center">Confirm Bulk Delete</h2>
                  <p className="text-gray-600 text-center mb-6">
                    Are you sure you want to delete <span className="font-bold">{selectedStudentIds.length}</span> selected students? This action cannot be undone.
                  </p>
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={() => setShowBulkDeleteConfirm(false)}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        await enhancedHandleBulkDelete(tenantId);
                        setShowBulkDeleteConfirm(false);
                        setSelectedStudentIds([]);
                        toast.success('Selected students deleted successfully!');
                      }}
                      className="px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center"
                    >
                      {bulkDeleteLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </>
                      ) : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </TenantAdminLayout>
  );
};

export default ManageStudents;